<template>
  <div ref="panel" class="sp-quantum-select-panel">
    <div class="sp-quantum-select-panel__header">
      <sp-search-box
        v-if="!hideSearchBar"
        v-model="searchText"
        v-preserve-properties="propertyBindings($props, searchBoxProps)"
        v-bind="attributeBindings($props, searchBoxProps)"
        density="compact"
        :input-description="remoteHelp"
        :focus-on-open="focusSearchOnOpen"
        @text-input="handleTextInput"
        @keydown.space.stop
        @keydown.enter.stop="handleSearchEnter"
      ></sp-search-box>
    </div>

    <div ref="scrollRoot" class="sp-quantum-select-panel__content" :aria-busy="remoteBusy" @scroll="handleScroll">
      <sp-list
        v-if="hasItems"
        ref="list"
        v-preserve-properties="propertyBindings(listBindings, listProps)"
        v-bind="attributeBindings(listBindings, listProps)"
        :aria-label="props.listLabel"
        scroll-selected-into-view
        @select-item="handleSelectItem"
        @update-selected="handleUpdateSelected"
        @append-action="handleAppendAction"
        @exit-start="reportExitStart"
      ></sp-list>
      <div v-else-if="showLocalLoading" class="sp-quantum-select-panel__loading">
        <sp-progress type="circular" size="x-large" aria-label="Loading"></sp-progress>
      </div>
      <div v-else-if="showLocalNoData" class="sp-quantum-select-panel__content-no-result">
        <slot name="no-data" />
      </div>
      <QuantumSelectRemoteStatus v-if="showRemoteStatus">
        <template #no-data><slot name="no-data" /></template>
      </QuantumSelectRemoteStatus>
    </div>
  </div>
</template>

<script setup>
import {
  LOADING_STATUSES,
  quantumSelectRemoteKey,
  RemoteStatus,
  SETTLED_STATUSES,
} from "#ce/components/SpQuantumSelect/composables/useQuantumSelectRemote";
import QuantumSelectRemoteStatus from "./QuantumSelectRemoteStatus.ce.vue";
import { remoteStatusText } from "#ce/components/SpQuantumSelect/i18n";
import { props as searchBoxProps } from "#ce/components/SpSearchBox/props";
import { attributeBindings, propertyBindings, vPreserveProperties } from "#ce/utils/property-bindings";
import { list as listProps } from "#ce/components/SpList/props";
/**
 * Panel renders a single level of a QuantumSelect: the search input, the list
 * of items for that level, and its loading and empty states. It holds no navigation state;
 * navigation containers (drilldown, flat, and so on) compose one or more panels.
 *
 * Private nested component of SpQuantumSelect; not a globally-registered custom element.
 */
import { useEventListener, watchImmediate } from "@vueuse/core";
import { computed, inject, nextTick, ref, useTemplateRef, watch } from "vue";
import { firstResultRow } from "#ce/components/SpQuantumSelect/composables/useQuantumSelectItems/mark-first-result.js";
import { panelProps } from "#ce/components/SpQuantumSelect/props.js";
import { usePanelPopupRole } from "#ce/components/SpQuantumSelect/composables/usePanelPopupRole";
import { useSpList } from "#ce/components/SpList/composables/useSpList";
import { isPresent, toBoolean } from "#ce/utils/props";
import { isObjectLike } from "#ce/utils/types";
import { t } from "#ce/shared/forms-i18n";

const remote = inject(quantumSelectRemoteKey, null);
const emit = defineEmits([
  "update-selected",
  "update-search",
  "next",
  "select-item",
  "search-enter",
  "exit-start",
  "update-status",
]);

const props = defineProps({
  ...panelProps,
  /**
   * Append-caret style for branch rows, forwarded to each sp-list-item. The
   * accordion container passes its host's `accordionCaretVariant`; drilldown
   * leaves the default "chevron".
   */
  caretVariant: {
    type: String,
    default: "chevron",
  },
  /**
   * Render branch-row carets with the in-place toggle mapping (down collapsed,
   * up expanded) instead of the drill-in mapping (right collapsed, down
   * expanded). Set by containers that expand rows in place (accordion).
   */
  caretToggle: {
    type: Boolean,
    default: false,
  },
});

// The field's v-model mirror. The `search` prop stays the container's
// programmatic channel (preset, clear), mirrored in through the watcher.
const searchText = ref(props.search ?? "");
function syncSearchText(value) {
  searchText.value = value ?? "";
}

function reportExitStart() {
  emit("exit-start");
}

watch(() => props.search, syncSearchText);

const loading = computed(() => toBoolean(props.loading));
const hideNoData = computed(() => toBoolean(props.hideNoData));
const remoteEnabled = computed(() => remote?.enabled.value);
const remoteStatus = computed(() => remote?.state.value.status);
const remoteI18nReady = computed(() => remote?.i18nReady.value);
const showRemoteStatus = computed(
  () => remoteEnabled.value && !(hideNoData.value && remoteStatus.value === RemoteStatus.EMPTY),
);
const showLocalLoading = computed(() => loading.value && !remoteEnabled.value);
const showLocalNoData = computed(() => !hideNoData.value && !remoteEnabled.value);
const hideSearchBar = computed(() => toBoolean(props.hideSearchBar));
const focusSearchOnOpen = computed(() => isPresent(props.focusSearchOnOpen));
const isDisabled = computed(() => toBoolean(props.disabled));
const isReadonly = computed(() => toBoolean(props.readonly));
const selectableBranches = computed(() => toBoolean(props.selectableBranches));
const branchToggleLabel = computed(() => props.branchToggleLabel);
const branchToggleTestId = computed(() => props.branchToggleTestId);
const caretVariant = computed(() => props.caretVariant);
const caretToggle = computed(() => props.caretToggle);
const itemTestId = computed(() => props.itemTestId);

const items = computed(() =>
  props.items?.map((item) => {
    if (isObjectLike(item)) {
      return { ...item, props: extendItemProps(item) };
    }
    return item;
  }),
);

// One binding object, so each update writes the list's `items` property once, with the extended rows.
const listBindings = computed(() => {
  const bindings = { ...props, items: items.value };
  delete bindings.listLabel;

  return bindings;
});

const panel = useTemplateRef("panel");
const scrollRoot = useTemplateRef("scrollRoot");
const remoteHelp = computed(() =>
  remoteEnabled.value && remoteI18nReady.value ? remoteText(remote.count.value) : undefined,
);
const remoteBusy = computed(() => remoteEnabled.value && LOADING_STATUSES.includes(remoteStatus.value));

function handleScroll(event) {
  const root = event.currentTarget;

  if (root.scrollTop > 0 && root.scrollHeight - root.scrollTop - root.clientHeight <= root.clientHeight / 4) {
    remote?.loadMore();
  }
}

let restoreFooterFocus = false;

function rememberFooterFocus() {
  restoreFooterFocus = !!panel.value?.getRootNode()?.activeElement?.closest?.(".sp-quantum-select-remote-status");
}

function forgetFooterFocus(event) {
  if (restoreFooterFocus && !event.composedPath().some(isFooter)) {
    restoreFooterFocus = false;
  }
}

function isFooter(node) {
  return !!node.classList?.contains("sp-quantum-select-remote-status");
}

function restoreFocus() {
  if (restoreFooterFocus) {
    const input = panel.value?.querySelector("sp-search-box")?.inputElement;

    if (input) {
      input.focus({ preventScroll: true });
    } else {
      remote?.focusActivator();
    }

    restoreFooterFocus = false;
  }
}

function remoteText(count) {
  return remoteStatusText(remote.state.value, remote.minimum.value, count);
}

function handleRemoteChange(state) {
  if (LOADING_STATUSES.includes(state.status)) {
    rememberFooterFocus();
  }

  if (SETTLED_STATUSES.includes(state.status) || state.status === RemoteStatus.ERROR) {
    nextTick(restoreFocus);
  }
}

if (remote) {
  watch(remote.state, handleRemoteChange, { flush: "sync" });
}

useEventListener(document, "focusin", forgetFooterFocus);

const hasItems = computed(() => items.value.length > 0);

const { getItemTitle } = useSpList(props);
const { isHeader, getItemProps } = useSpList({ itemProps: listProps.itemProps.default });

usePanelPopupRole({
  panel,
  items,
  readonly: () => props.pickerReadonly ?? props.readonly,
  tree: () => props.tree,
  loading,
  isHeader,
  getItemProps,
});

// What a search actually turned up. `items` also carries section headers, pinned action rows that
// survive any query, and fallback rows that appear only when nothing matched — none of which are
// results, and counting the last would report a hit on exactly the dead-end query that has none.
const matchedCount = computed(
  () =>
    items.value.filter((item) => {
      if (!isObjectLike(item)) {
        return true;
      }
      return !("header" in item) && !item.pinned && !item.fallback;
    }).length,
);

// Whether focus is inside the list, on a row or on the list itself. Enter then commits the focused
// row, or nothing, so the field's suggestion is withdrawn. The list element hears focus only when it
// crosses its boundary, so a move from a row to the list itself keeps the flag as it is.
const list = useTemplateRef("list");
const listHasFocus = ref(false);

function noteListFocus() {
  listHasFocus.value = true;
}

function noteListBlur() {
  listHasFocus.value = false;
}

useEventListener(list, "focusin", noteListFocus);
useEventListener(list, "focusout", noteListBlur);

// The title of the row Enter in the field commits, under `field-commits-highlight`. The arrows step
// past that row, and its mark is visual, so the status is where a screen reader hears its name.
const suggestedTitle = computed(() => {
  if (!toBoolean(props.fieldCommitsHighlight) || listHasFocus.value) {
    return undefined;
  }

  const suggested = firstResultRow(items.value, { honourMarked: true, markedOnly: true });
  const title = suggested ? getItemTitle(suggested) : undefined;

  return title == null || title === "" ? undefined : title;
});

// Reported upward rather than rendered here: this panel is remounted by the drilldown's frame
// transition, and a live region inserted with its content announces nothing.
const resultStatus = computed(() => {
  // The root announces every other remote status.
  if (remoteEnabled.value) {
    if (remoteStatus.value !== RemoteStatus.READY || suggestedTitle.value === undefined) {
      return "";
    }

    return t("results_count_with_suggestion", { count: remote.count.value, title: suggestedTitle.value });
  }

  // Mirrors which branch the panel renders: rows win over the spinner, so a panel still loading
  // more on top of rows it already shows is announced by its count, not as loading.
  if (loading.value && !hasItems.value) {
    return t("results_loading");
  }

  // A query that matched nothing can still leave a fallback or pinned row for Enter to commit.
  if (suggestedTitle.value !== undefined) {
    return t("results_count_with_suggestion", { count: matchedCount.value, title: suggestedTitle.value });
  }

  return matchedCount.value === 0 ? t("results_none") : t("results_count", { count: matchedCount.value });
});

function reportResultStatus(status) {
  emit("update-status", status);
}

watchImmediate(resultStatus, reportResultStatus);

function extendItemProps(item) {
  const props = {
    ...(item.props ?? {}),
  };
  // Shared row hook for the QA selector catalog; a per-item value wins.
  if (itemTestId.value != null && props["data-testid"] == null) {
    props["data-testid"] = itemTestId.value;
  }
  // Forward a top-level `disabled` flag so a disabled item shaped like
  // `{ title, value, disabled: true }` reaches sp-list as a per-item prop.
  // Coerce with toBoolean so string forms (e.g. "true") match sp-list's gate.
  if (isDisabled.value || toBoolean(item.disabled)) {
    props.disabled = true;
  }
  // sp-list-item reflects a removed string prop as "" (which fails its validator),
  // so the caret variant is always set to a valid value, even for leaf rows.
  props.appendCaretVariant = caretVariant.value;
  if (item.hasChildren) {
    // Render a rotating chevron caret (SpCaret) in the append region. In-place
    // containers (accordion) set `caretToggle` so the chevron points down and
    // rotates up when the row's `expanded` flag is set; navigation containers
    // (drilldown) leave both unset so it points right.
    props.appendCaret = true;
    props.appendCaretExpanded = !!item.expanded;
    props.appendCaretToggle = caretToggle.value;
    if (selectableBranches.value) {
      // Branch is a real selectable value; its children toggle from a dedicated
      // interactive caret (which swallows the click so selecting ≠ toggling).
      props.selectable = true;
      props.appendCaretInteractive = true;
      props.appendCaretLabel = branchToggleLabel.value;
      if (branchToggleTestId.value != null) {
        props.appendCaretTestId = branchToggleTestId.value;
      }
    } else {
      // Classic accordion: the whole row toggles expansion and is not selectable.
      props.selectable = false;
    }
  }
  return props;
}

function handleTextInput({ detail }) {
  emit("update-search", ...detail);
}

// Enter in the search box belongs to the combobox, not the form: surface it so
// a host can commit its highlighted row (the in-sheet search bar's only
// Enter path).
function handleSearchEnter(event) {
  emit("search-enter", event);
}

function handleUpdateSelected({ detail }) {
  const [value] = detail;

  return emit("update-selected", value);
}

// The dedicated branch disclosure caret was clicked (selectableBranches mode).
// `sp-list` is a custom element, so its emit arrives as a CustomEvent whose
// detail carries the emit args (same shape handleSelectItem/handleUpdateSelected read).
// Toggle expansion via the same `next` channel the classic accordion uses; the
// click was swallowed by sp-list-item, so no selection fires alongside it.
function handleAppendAction({ detail }) {
  const [item] = detail;
  emit("next", item);
}

function handleSelectItem({ detail }) {
  const [item] = detail;

  // Classic accordion: a branch row toggles expansion. With selectableBranches
  // the row body selects instead and the dedicated caret (→ handleAppendAction)
  // owns toggling, so fall through to the normal selection path here.
  if (item?.hasChildren && !selectableBranches.value) {
    emit("next", item);
    return;
  }

  // Mirror SpList's selection gate: a readonly list or an item flagged
  // `selectable: false` / `disabled: true` updates nothing, so the full-item
  // event must not fire either — consumers route navigation/filters off it.
  // (SpList emits its raw `select-item` unconditionally so drill-in still works
  // for hasChildren rows, which are themselves `selectable: false`.)
  if (isReadonly.value || item?.props?.selectable === false || toBoolean(item?.props?.disabled)) {
    return;
  }

  // Clicking an already-selected row toggles it OFF — SpList emits its raw
  // `select-item` before applying that, and `update-selected` will clear the
  // value. So suppress the full-item event for a deselect; it should only fire
  // when the click actually selects/adds the item.
  if (isSelectedValue(item)) {
    reportReselection();
    return;
  }

  // Terminal (leaf) selection: surface the full item so the host can re-emit it
  // for consumers that need the whole payload, not just the selected value.
  emit("select-item", item);
}

// A required single select's list reports nothing on a re-click, so the unchanged selection is reported here.
function reportReselection() {
  if (isPresent(props.required) && !toBoolean(props.multiple)) {
    emit("update-selected", props.selected);
  }
}

// Whether the item's value is in the current selection (so a click on it would
// deselect rather than select). `props.selected` reflects the pre-click state.
function isSelectedValue(item) {
  const selected = Array.isArray(props.selected) ? props.selected : [];
  if (selected.length === 0) {
    return false;
  }
  const value = isObjectLike(item) ? item[props.itemValue] : item;
  return selected.some((entry) => String(entry) === String(value));
}
</script>

<style lang="scss" scoped>
sp-search-box {
  margin-inline: var(--quantum-select-content-padding);
}

.sp-quantum-select-panel {
  position: relative;
  display: grid;
  grid-template-rows: auto 1fr;
  max-height: 100%;
  height: 100%;
  min-width: var(--quantum-select-popup-width);
}

.sp-quantum-select-panel__header:not(:empty) {
  margin-bottom: var(--sp-comp-quantum-select-panel-header-gap, var(--sp-ref-scale-12, 0.75rem));
}

.sp-quantum-select-panel__content {
  position: relative;
  overflow-y: var(--sp-comp-quantum-select-panel-content-overflow-y, auto);
}

.sp-quantum-select-panel__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.sp-quantum-select-panel__content-no-result {
  padding: var(--sp-comp-quantum-select-panel-no-result-padding, 1rem);
  color: var(--sp-comp-quantum-select-panel-no-result-color, #aaa);
}

sp-list {
  --list-item-inline-padding: var(--quantum-select-content-padding);
  // Inset the append region by the panel's content padding so the chevron
  // caret's right edge lines up with the search box and row dividers — mirroring
  // the prepend icon's left inset. (The previous `0` was calibrated for the old
  // `chevron-right-filled` glyph, whose internal SVG whitespace faked the inset;
  // SpCaret draws closer to its box edge, so it needs the real margin.)
  --sp-comp-list-item-append-margin-right: var(--quantum-select-content-padding);
  --sp-comp-list-item-prepend-icon-fill-hover: #777;
}
</style>
